/**
 * CustomRuleEditor Component
 * Modal/form for creating and editing custom validation rules
 */

import React, { useState, useEffect } from "react";
import type { RuleTemplate, RuleParameter } from "../../types/rules";

interface CustomRuleEditorProps {
  rule?: RuleTemplate | null;
  onSave: (rule: RuleTemplate) => void;
  onCancel: () => void;
}

export function CustomRuleEditor({
  rule,
  onSave,
  onCancel,
}: CustomRuleEditorProps) {
  // Initialize with one default parameter if creating a new rule
  const defaultParameter: RuleParameter = {
    id: `param-${Date.now()}`,
    label: "New Parameter",
    type: "number",
    value: 0,
  };

  const [name, setName] = useState(rule?.name || "");
  const [category, setCategory] = useState(rule?.category || "Uncategorized");
  const [description, setDescription] = useState(rule?.description || "");
  const [standardName, setStandardName] = useState(rule?.standard || "");
  const [standardType, setStandardType] = useState("");
  const [elementTypes, setElementTypes] = useState(
    rule?.elementTypes?.join(", ") || ""
  );
  const [requiredElements, setRequiredElements] = useState("");
  const [location, setLocation] = useState("");
  const [severity, setSeverity] = useState<
    "low" | "medium" | "high" | "critical"
  >("medium");
  const [runRule, setRunRule] = useState(false);
  const [parameters, setParameters] = useState<RuleParameter[]>(
    rule?.parameters && rule.parameters.length > 0
      ? rule.parameters
      : [defaultParameter]
  );

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setCategory(rule.category);
      setDescription(rule.description);
      setStandardName(rule.standard || "");
      setElementTypes(rule.elementTypes?.join(", ") || "");
      // Initialize new fields (these would come from rule metadata if we extend the type)
      setRequiredElements("");
      setLocation("");
      setSeverity("medium");
      setRunRule(false);
      // If editing a rule with no parameters, show one default parameter
      if (rule.parameters && rule.parameters.length > 0) {
        setParameters(rule.parameters);
      } else {
        const defaultParam: RuleParameter = {
          id: `param-${Date.now()}`,
          label: "New Parameter",
          type: "number",
          value: 0,
        };
        setParameters([defaultParam]);
      }
    }
  }, [rule]);

  const handleAddParameter = () => {
    const newParam: RuleParameter = {
      id: `param-${Date.now()}`,
      label: "New Parameter",
      type: "number",
      value: 0,
    };
    setParameters([...parameters, newParam]);
  };

  const handleRemoveParameter = (paramId: string) => {
    setParameters(parameters.filter((p) => p.id !== paramId));
  };

  const handleUpdateParameter = (
    paramId: string,
    updates: Partial<RuleParameter>
  ) => {
    setParameters(
      parameters.map((p) => (p.id === paramId ? { ...p, ...updates } : p))
    );
  };

  const handleSave = () => {
    // Auto-generate rule name if not provided, using Standard Name or a default
    const ruleName =
      name.trim() ||
      standardName.trim() ||
      `Custom Rule ${new Date().toLocaleString()}`;

    const ruleTemplate: RuleTemplate = {
      id:
        rule?.id ||
        `custom-rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: ruleName,
      category: category.trim() || "Uncategorized",
      description:
        description.trim() ||
        `Custom validation rule for ${standardName.trim() || "compliance"}`,
      standard: standardName.trim() || "Custom",
      elementTypes: elementTypes
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
      parameters: parameters,
      enabled: runRule, // Set enabled based on "Run Rule" checkbox
    };

    // Store additional metadata (we'll need to extend the store to handle these)
    // For now, we can store them in a custom property or extend the type
    const ruleWithMetadata = {
      ...ruleTemplate,
      metadata: {
        requiredElements: requiredElements
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0),
        location: location.trim(),
        severity: severity,
        standardType: standardType.trim(),
      },
    };

    onSave(ruleTemplate);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {rule ? "Edit Custom Rule" : "Create Custom Rule"}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6 bg-white">
          {/* Rule Configuration */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Standard Name
              </label>
              <input
                type="text"
                value={standardName}
                onChange={(e) => setStandardName(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., IBC, ADA, NFPA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Standard Type
              </label>
              <select
                value={standardType}
                onChange={(e) => setStandardType(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select type...</option>
                <option value="Building Code">Building Code</option>
                <option value="Accessibility">Accessibility</option>
                <option value="Fire Safety">Fire Safety</option>
                <option value="Custom">Custom</option>
                <option value="Company Standard">Company Standard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Elements
              </label>
              <input
                type="text"
                value={requiredElements}
                onChange={(e) => setRequiredElements(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Wall, Door, Window"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Interior, Exterior, All"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) =>
                  setSeverity(
                    e.target.value as "low" | "medium" | "high" | "critical"
                  )
                }
                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={runRule}
                  onChange={(e) => setRunRule(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                Run Rule (Enable this rule for validation)
              </label>
            </div>
          </div>

          {/* Parameters */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-800">Parameters</h3>
              <button
                onClick={handleAddParameter}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Add Parameter
              </button>
            </div>

            {parameters.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                <p>No parameters yet. Add parameters to configure this rule.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {parameters.map((param, index) => (
                  <div
                    key={param.id}
                    className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200"
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-800">
                        Parameter {index + 1}
                      </span>
                      <button
                        onClick={() => handleRemoveParameter(param.id)}
                        className="text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={param.label}
                          onChange={(e) =>
                            handleUpdateParameter(param.id, {
                              label: e.target.value,
                            })
                          }
                          className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={param.type}
                          onChange={(e) =>
                            handleUpdateParameter(param.id, {
                              type: e.target.value as RuleParameter["type"],
                              value:
                                e.target.value === "boolean"
                                  ? false
                                  : e.target.value === "number"
                                    ? 0
                                    : "",
                            })
                          }
                          className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="number">Number</option>
                          <option value="string">String</option>
                          <option value="boolean">Boolean</option>
                          <option value="select">Select</option>
                        </select>
                      </div>

                      {param.type === "number" && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Default Value
                            </label>
                            <input
                              type="number"
                              value={param.value}
                              onChange={(e) =>
                                handleUpdateParameter(param.id, {
                                  value: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Unit
                            </label>
                            <input
                              type="text"
                              value={param.unit || ""}
                              onChange={(e) =>
                                handleUpdateParameter(param.id, {
                                  unit: e.target.value,
                                })
                              }
                              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="e.g., inches, feet"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Min
                            </label>
                            <input
                              type="number"
                              value={param.min || ""}
                              onChange={(e) =>
                                handleUpdateParameter(param.id, {
                                  min: parseFloat(e.target.value) || undefined,
                                })
                              }
                              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Max
                            </label>
                            <input
                              type="number"
                              value={param.max || ""}
                              onChange={(e) =>
                                handleUpdateParameter(param.id, {
                                  max: parseFloat(e.target.value) || undefined,
                                })
                              }
                              className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </>
                      )}

                      {param.type === "string" && (
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Default Value
                          </label>
                          <input
                            type="text"
                            value={param.value || ""}
                            onChange={(e) =>
                              handleUpdateParameter(param.id, {
                                value: e.target.value,
                              })
                            }
                            className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}

                      {param.type === "boolean" && (
                        <div className="col-span-2">
                          <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={param.value || false}
                              onChange={(e) =>
                                handleUpdateParameter(param.id, {
                                  value: e.target.checked,
                                })
                              }
                              className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            Default Value (checked = true)
                          </label>
                        </div>
                      )}

                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={param.description || ""}
                          onChange={(e) =>
                            handleUpdateParameter(param.id, {
                              description: e.target.value,
                            })
                          }
                          className="w-full px-2 py-1.5 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Parameter description/help text"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            {rule ? "Update Rule" : "Create Rule"}
          </button>
        </div>
      </div>
    </div>
  );
}
